import { FC, forwardRef, ReactNode } from 'react';

import { SPACE } from 'utils/constants/common';
import { VIEW_DATE_WITH_TIME_FORMAT } from 'utils/constants/date';
import { Permissions } from 'utils/constants/permissions';
import { formatDate } from 'utils/date';

import { Button, Link, TableCell, TableRow, Text, VSpacingContainer } from '@hh.ru/magritte-ui';
import { GearOutlinedSize16, LineArrowRightEnterOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { AccountsLink } from 'components/Admin/AccountsLink/AccountsLink';
import { fetchSudo } from 'components/Admin/Users/User/fetchSudo';

import { UserFilterType } from 'store/models/user';
import { Account, UserDetail, UserProvider } from 'store/models/userDetail';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    employerID: 'admin.Users.User.employerID',
    hhID: 'admin.Users.User.hhID',
    id: 'admin.Users.User.id',
    accountId: 'admin.Users.User.accountId',
    email: 'admin.Users.User.email',
    name: 'admin.Users.User.name',
    lastActivityTime: 'admin.Users.User.lastActivityTime',
    providersEmpty: 'admin.Users.User.Providers.empty',
    provider: (source: string) => `admin.Users.User.Providers.${source}`,
    managerId: 'admin.Users.User.managerId',
    managerIdEmpty: 'admin.Users.User.managerId.empty',
    managerEmail: 'admin.Users.User.managerEmail',
    managerEmailEmpty: 'admin.Users.User.managerEmail.empty',
    edit: 'admin.Users.User.edit',
    sudo: 'admin.Users.User.sudo',
};

interface InfoRowProps {
    label: string;
    children: ReactNode;
}

const InfoRow: FC<InfoRowProps> = ({ label, children }) => (
    <div>
        <Text Element="span" typography="label-3-regular" style="secondary">
            {label}
        </Text>
        {SPACE}
        <Text Element="span" typography="label-3-regular" style="primary">
            {children}
        </Text>
    </div>
);

export interface UsersTableRowProps extends UserDetail {
    hideActions?: boolean;
}

export const UsersTableRow = forwardRef<HTMLTableRowElement, UsersTableRowProps>(
    (
        {
            id,
            employerId,
            hhid,
            accounts,
            email,
            name,
            lastActivityTime,
            personalManagerId,
            personalManagerEmail,
            disabled,
            providers,
            hideActions = false,
        },
        ref
    ) => {
        const permissions = useSelector((state) => state.user.permissions);
        const filterType = useSelector((state) => state.users.filterType);
        const filterValue = useSelector((state) => state.users.filterValue);
        const [isLoading, setIsLoadingOn, setIsLoadingOff] = useOnOffState(false);

        const dispatch = useDispatch();
        const trl = useTrls();

        const loginAsSudo = () => {
            if (id === null) {
                return;
            }

            const matchedAccount =
                filterType === UserFilterType.AccountId &&
                filterValue &&
                accounts?.some((account: Account) => String(account.id) === filterValue);

            const accountId = matchedAccount ? Number(filterValue) : null;

            setIsLoadingOn();
            void dispatch(fetchSudo(id, accountId, setIsLoadingOff));
        };

        return (
            <TableRow
                ref={ref}
                key={id}
                style={disabled ? 'negative-secondary' : undefined}
                data-qa={`user-row-${String(id)}`}
            >
                <TableCell align="left" verticalAlign="top">
                    <VSpacingContainer default={4}>
                        <InfoRow label={trl(TrlKeys.employerID)}>
                            {employerId !== null && employerId !== 0 && (
                                <Link href={`https://hh.ru/employer/${employerId}`} target="_blank" inline>
                                    {employerId}
                                </Link>
                            )}
                        </InfoRow>
                        <InfoRow label={trl(TrlKeys.hhID)}>{hhid}</InfoRow>
                        <InfoRow label={trl(TrlKeys.id)}>{id}</InfoRow>
                        <InfoRow label={trl(TrlKeys.accountId)}>
                            {id !== null && <AccountsLink accounts={accounts} userId={id} />}
                        </InfoRow>
                    </VSpacingContainer>
                </TableCell>
                <TableCell align="left" verticalAlign="top">
                    <VSpacingContainer default={4}>
                        <InfoRow label={trl(TrlKeys.email)}>
                            <Link href={`mailto:${email}`} inline>
                                {email}
                            </Link>
                        </InfoRow>
                        <InfoRow label={trl(TrlKeys.name)}>{name}</InfoRow>
                        <InfoRow label={trl(TrlKeys.lastActivityTime)}>
                            {formatDate(lastActivityTime, VIEW_DATE_WITH_TIME_FORMAT)}
                        </InfoRow>
                        {(!providers || providers.length === 0) && (
                            <Text typography="label-3-regular" style="secondary">
                                {trl(TrlKeys.providersEmpty)}
                            </Text>
                        )}
                        {providers?.map(({ id: providerId, source }: UserProvider) => (
                            <InfoRow key={source} label={trl(TrlKeys.provider(source))}>
                                {providerId}
                            </InfoRow>
                        ))}
                    </VSpacingContainer>
                </TableCell>
                <TableCell align="left" verticalAlign="top">
                    <VSpacingContainer default={4}>
                        {personalManagerId ? (
                            <InfoRow label={trl(TrlKeys.managerId)}>{personalManagerId}</InfoRow>
                        ) : (
                            <Text typography="label-3-regular" style="secondary">
                                {trl(TrlKeys.managerIdEmpty)}
                            </Text>
                        )}
                        {personalManagerEmail ? (
                            <InfoRow label={trl(TrlKeys.managerEmail)}>
                                <Link href={`mailto:${personalManagerEmail}`} inline>
                                    {personalManagerEmail}
                                </Link>
                            </InfoRow>
                        ) : (
                            <Text typography="label-3-regular" style="secondary">
                                {trl(TrlKeys.managerEmailEmpty)}
                            </Text>
                        )}
                    </VSpacingContainer>
                </TableCell>
                {!hideActions && (
                    <TableCell align="left" verticalAlign="top">
                        <VSpacingContainer default={8}>
                            {permissions.includes(Permissions.ViewAdminEditUser) && id !== null && (
                                <Button
                                    Element="a"
                                    href={`/new/admin/users/${id}/`}
                                    mode="secondary"
                                    style="neutral"
                                    size="small"
                                    icon={<GearOutlinedSize16 />}
                                    data-qa="edit-user-button"
                                >
                                    {trl(TrlKeys.edit)}
                                </Button>
                            )}
                            <Button
                                mode="primary"
                                style="accent"
                                size="small"
                                icon={<LineArrowRightEnterOutlinedSize16 />}
                                disabled={disabled || id === null}
                                onClick={loginAsSudo}
                                data-qa="user-sudo"
                                loading={isLoading}
                            >
                                {trl(TrlKeys.sudo)}
                            </Button>
                        </VSpacingContainer>
                    </TableCell>
                )}
            </TableRow>
        );
    }
);

UsersTableRow.displayName = 'UsersTableRow';
